"use client";

import { useId } from "react";
import { cn } from "@/lib/utils/cn";

interface ConvictionSliderProps {
  value: number;
  onChange: (n: number) => void;
  suggestion?: number;
  disabled?: boolean;
  className?: string;
  "aria-label"?: string;
  /** Hide the (?) explainer popover. Default: shown. */
  hideExplainer?: boolean;
}

export function ConvictionSlider({
  value,
  onChange,
  suggestion,
  disabled,
  className,
  "aria-label": ariaLabel,
  hideExplainer,
}: ConvictionSliderProps) {
  const explainerId = useId();
  const showSuggestion =
    typeof suggestion === "number" &&
    Number.isInteger(suggestion) &&
    suggestion >= 1 &&
    suggestion <= 10 &&
    suggestion !== value;

  return (
    <div className={cn("flex flex-col gap-1.5", className)}>
      <div className="flex items-center gap-3">
        <input
          type="range"
          min={1}
          max={10}
          step={1}
          value={value}
          disabled={disabled}
          aria-label={ariaLabel ?? "Conviction"}
          aria-describedby={hideExplainer ? undefined : explainerId}
          aria-valuemin={1}
          aria-valuemax={10}
          aria-valuenow={value}
          onChange={(e) => onChange(Number(e.target.value))}
          className={cn(
            "h-2 flex-1 cursor-pointer appearance-none rounded-full bg-muted",
            "disabled:cursor-not-allowed disabled:opacity-50",
          )}
        />
        <span className="w-6 text-right text-sm font-medium tabular-nums">
          {value}
        </span>
        {!hideExplainer ? <ConvictionExplainer id={explainerId} /> : null}
      </div>
      {showSuggestion ? (
        <button
          type="button"
          onClick={() => onChange(suggestion!)}
          disabled={disabled}
          className={cn(
            "self-start text-xs text-muted-foreground hover:text-foreground",
            "underline underline-offset-2 transition-colors",
            "disabled:cursor-not-allowed disabled:opacity-50",
          )}
        >
          AI suggests {suggestion}
        </button>
      ) : null}
    </div>
  );
}

/**
 * Conviction explainer popover — uses native <details> to stay HTML-only /
 * keyboard accessible / no client state. Mirrors the TierExplainer pattern
 * in tier-select.tsx for visual consistency.
 */
function ConvictionExplainer({ id }: { id: string }) {
  return (
    <details className="relative inline-block">
      <summary
        aria-label="What does the conviction score mean?"
        className={cn(
          "list-none cursor-pointer select-none flex items-center justify-center",
          "w-5 h-5 rounded-full border border-surface-light/40 bg-transparent",
          "text-[11px] font-bold text-muted-foreground hover:text-foreground hover:border-blue/40",
          "transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring",
          // Hide the disclosure marker in webkit
          "[&::-webkit-details-marker]:hidden",
        )}
      >
        ?
      </summary>
      <div
        id={id}
        role="region"
        aria-label="Conviction explainer"
        className={cn(
          "absolute right-0 z-50 mt-2 w-[300px] max-w-[80vw]",
          "rounded-xl border border-surface-light/30 bg-surface shadow-2xl p-3",
          "text-[11px] leading-relaxed text-foreground/80 space-y-2",
        )}
      >
        <p className="font-semibold text-foreground">
          Your conviction in this thesis (1-10).
        </p>
        <p>
          <span className="font-semibold text-muted-foreground">1-3: Skeptical / placeholder.</span>{" "}
          You added it to track but you don&apos;t believe the bull case yet.
        </p>
        <p>
          <span className="font-semibold text-blue">4-6: Neutral.</span>{" "}
          You think the thesis is plausible but aren&apos;t acting on it.
        </p>
        <p>
          <span className="font-semibold text-gold">7-8: Confident.</span>{" "}
          You believe in the bull case; this is a real position you&apos;d size up.
        </p>
        <p>
          <span className="font-semibold text-gold">9-10: High conviction.</span>{" "}
          This is one of your best ideas. You&apos;d buy more on weakness.
        </p>
        <p className="pt-1 border-t border-surface-light/20 text-muted-foreground">
          The AI may suggest bumping conviction up or down based on weekly
          analysis. You stay in control.
        </p>
      </div>
    </details>
  );
}
